import { useCallback, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAccount } from 'wagmi'
import { T } from '@/lib/theme'
import { Panel, SectionLabel, Skeleton, Stat, ToggleButton } from '@/components/ui'
import { TokenGlyph } from '@/components/dashboard/TokenGlyph'
import { DetailPageShell, SubTabs } from '@/components/dashboard/detail/DetailPageShell'
import { StatGrid } from '@/components/dashboard/detail/StatGrid'
import { FaqAccordion } from '@/components/dashboard/detail/FaqAccordion'
import { CurveChart } from '@/components/dashboard/detail/CurveChart'
import { MiniLineChart } from '@/components/dashboard/MiniLineChart'
import {
  SupplyForm, BorrowForm, FacilitatorSupplyNote, CollateralOnlyNote, IsolationWarnings, DropToLiquidation,
} from '@/components/dashboard/markets/ReserveForms'
import { useReserveDetail, buildRateCurve } from '@/hooks/useReserveDetail'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { useReserveHistory } from '@/hooks/useReserveHistory'
import { useReserveActivity } from '@/hooks/useReserveActivity'
import { usePriceFeedInfo } from '@/hooks/usePriceFeedInfo'
import { useLiveAccrual } from '@/hooks/useLiveAccrual'
import { useHorizonPriceUsd } from '@/hooks/useHorizonPriceUsd'
import { reserveFaq, fmtDuration } from '@/lib/faqContent'
import { accountRisk } from '@/lib/accountRisk'
import { fmt, fmtBalance, fmtUsd, fmtPct, toNumber, fmtBps } from '@/lib/utils'
import { addressLink } from '@/lib/contracts'

// ─────────────────────────────────────────────────────────────────
// Per-reserve detail page. Layered guards first, exactly as VaultDetailSection does, so an unknown
// address in the URL resolves to an honest "not found" rather than a blank or crashing page.
// ─────────────────────────────────────────────────────────────────

export function ReserveDetailSection({ asset }) {
  const { reserve, isLoading, notFound, notDeployed, refetch, d } = useReserveDetail(asset)

  if (notDeployed) {
    return <NoticeCard title="This market is live on another network."
      body="Point your wallet at a network where Horizon's lending pool is deployed." />
  }
  if (notFound) {
    return <NoticeCard title="Market not found."
      body="Horizon's pool lists a different set of assets. Double-check the address in your URL." />
  }
  if (isLoading || !reserve) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
        <Skeleton width={280} height={32} />
        <div style={cardsRow}><Skeleton width="100%" height={90} /><Skeleton width="100%" height={90} /><Skeleton width="100%" height={90} /></div>
      </div>
    )
  }

  return <ReserveDetail reserve={reserve} d={d} refetch={refetch} />
}

function NoticeCard({ title, body }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Link to="/markets" style={backLinkStyle}>← Markets</Link>
      <Panel elevated style={{ padding: 40, textAlign: 'center' }}>
        <div style={{ color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.ui, fontWeight: 700 }}>{title}</div>
        <div style={{ marginTop: 8, color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.body, lineHeight: 1.6 }}>{body}</div>
      </Panel>
    </div>
  )
}

function ReserveDetail({ reserve, d, refetch }) {
  const { address: user } = useAccount()
  // Below this the 380px rail leaves the parameter grid too narrow to read in two columns, so the
  // rail drops beneath the content instead. Inline styles carry no media queries, which is why
  // this is a hook rather than CSS - the same approach TopBar and SkyBackground already use.
  const narrow = useMediaQuery('(max-width: 1100px)')
  // Reported up from whichever form the user is typing in, so the rate curve can mark where that
  // amount would move utilisation. `useCallback` because it lands in the forms' effect deps.
  const [projection, setProjection] = useState(null)
  const onProjection = useCallback((p) => setProjection(p), [])

  const badges = [
    reserve.isIsolated ? { label: 'Isolated', color: T.warning } : null,
    reserve.isFacilitator ? { label: 'Mint-only', color: T.accent } : null,
    reserve.isPaused ? { label: 'Paused', color: T.negative } : null,
  ].filter(Boolean)

  const meta = [
    { label: `${reserve.symbol} price`, value: fmtUsd(reserve.priceUsd) },
    { label: 'Price valid for', value: fmtDuration(reserve.priceStaleAfterSec) },
  ]

  return (
    <DetailPageShell
      backTo="/markets"
      backLabel="Back to Markets"
      breadcrumb={[{ label: 'Markets', to: '/markets' }, { label: `${reserve.symbol} Market` }]}
      glyph={<TokenGlyph symbol={reserve.symbol} size={36} />}
      title={`${reserve.symbol} Market`}
      badges={badges}
      meta={meta}
      tabs={[
        { id: 'overview', label: 'Overview' },
        { id: 'position', label: 'My Position' },
        { id: 'info', label: 'Info & FAQs' },
      ]}
    >
      {(tab) => {
        const mainCol = (
          <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 20 }}>
            {tab === 'overview' && <OverviewTab reserve={reserve} projection={projection} />}
            {tab === 'position' && <PositionTab reserve={reserve} d={d} user={user} />}
            {tab === 'info' && <InfoTab reserve={reserve} />}
          </div>
        )
        const railCol = (
          <div style={rail}>
            <div style={{ position: 'sticky', top: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
              <IsolationWarnings reserve={reserve} d={d} user={user} />
              <ReserveActionRail reserve={reserve} d={d} user={user} refetch={refetch} onProjection={onProjection} />
            </div>
          </div>
        )
        // On a narrow screen the two-column grid collapses to one; put the ACTION rail FIRST so a
        // phone user reaches Supply/Borrow immediately instead of scrolling past the whole overview.
        // On desktop the informational main column leads and the sticky rail sits to its right.
        return (
          <div style={narrow ? layoutNarrow : layout}>
            {narrow ? <>{railCol}{mainCol}</> : <>{mainCol}{railCol}</>}
          </div>
        )
      }}
    </DetailPageShell>
  )
}

/// The action rail: ONE form at a time behind a Supply/Borrow segmented control, instead of stacking
/// both full form panels (which doubled the rail height and repeated the health-factor /
/// available-to-borrow rows across both). Collateral-only reserves have no borrow side, so they skip
/// the toggle and show Supply alone. The forms are self-contained panels, so the toggle sits above
/// them rather than wrapping them.
function ReserveActionRail({ reserve, d, user, refetch, onProjection }) {
  const [mode, setMode] = useState('supply')
  const supplyEl = reserve.isFacilitator
    ? <FacilitatorSupplyNote reserve={reserve} />
    : <SupplyForm reserve={reserve} d={d} user={user} onTx={refetch} onProjection={onProjection} />

  // No borrow side: show Supply plus the collateral-only note, no toggle.
  if (reserve.borrowingDisabled) {
    return <>{supplyEl}<CollateralOnlyNote reserve={reserve} /></>
  }

  return (
    <>
      <div style={{ display: 'flex', gap: 8 }}>
        <ToggleButton size="lg" active={mode === 'supply'} onClick={() => setMode('supply')}>Supply</ToggleButton>
        <ToggleButton size="lg" active={mode === 'borrow'} onClick={() => setMode('borrow')}>Borrow</ToggleButton>
      </div>
      {mode === 'supply'
        ? supplyEl
        : <BorrowForm reserve={reserve} d={d} user={user} onTx={refetch} onProjection={onProjection} />}
    </>
  )
}

// ─────────────────────────────────────────────────────────────────
// Overview
// ─────────────────────────────────────────────────────────────────

function OverviewTab({ reserve, projection }) {
  const [view, setView] = useState('market')

  return (
    <>
      <div style={cardsRow}>
        <Stat boxed label="Total supplied" value={fmtUsd(reserve.totalSupplied * reserve.priceUsd)} sub={`${fmt(reserve.totalSupplied, 2)} ${reserve.symbol}`} />
        <Stat boxed label="Total borrowed" value={fmtUsd(reserve.totalBorrows * reserve.priceUsd)} sub={`${fmt(reserve.totalBorrows, 2)} ${reserve.symbol}`} />
        <Stat boxed label="Available liquidity" value={fmtUsd(reserve.availableLiquidity * reserve.priceUsd)} sub={`${fmt(reserve.availableLiquidity, 2)} ${reserve.symbol}`} />
      </div>

      <Panel style={{ padding: 28 }}>
        <div style={panelHeader}>
          <SectionLabel>Market parameters</SectionLabel>
          <SubTabs
            tabs={[{ id: 'market', label: 'Market' }, { id: 'risk', label: 'Risk & caps' }]}
            active={view}
            onSelect={setView}
          />
        </div>

        <div style={{ marginTop: 18 }}>
          {view === 'market' ? (
            <StatGrid items={[
              { label: 'Supply APY', value: fmtBps(reserve.totalSupplyAprBps), color: T.positive },
              { label: 'Borrow APR', value: fmtBps(reserve.totalBorrowAprBps) },
              { label: 'Utilisation', value: fmtBps(reserve.utilizationBps) },
              { label: 'Reserve factor', value: fmtBps(reserve.reserveFactorBps), help: 'The share of borrower interest the protocol keeps. Suppliers receive the rest.' },
              { label: 'Rate model', value: reserve.useAdaptiveIrm ? 'Adaptive' : 'Kinked', help: reserve.useAdaptiveIrm ? 'An adaptive curve that retunes to demand on its own, so it needs no governance intervention when utilisation shifts.' : 'A fixed base + slope curve with a kink; retuned by governance.' },
              // Adaptive shows its target + rate-at-target; the kinked fallback shows base + kink.
              ...(reserve.useAdaptiveIrm
                ? [
                    { label: 'Target utilisation', value: fmtBps(reserve.adaptiveTargetBps), help: 'The utilisation the curve pivots at and drifts toward. Above it rates rise, below it they ease.' },
                    { label: 'Rate at target', value: fmtBps(reserve.rateAtTargetBps), help: 'The current borrow rate at exactly the target utilisation. This is the value that adapts over time.' },
                  ]
                : [
                    { label: 'Base rate', value: fmtBps(reserve.baseRateBps) },
                    { label: 'Kink', value: fmtBps(reserve.kinkBps), help: 'The utilisation past which the rate climbs steeply, which is what keeps liquidity available for withdrawals.' },
                  ]),
              { label: 'HORIZON to suppliers', value: reserve.supplyHorizonAprBps > 0 ? fmtBps(reserve.supplyHorizonAprBps) : '—' },
              { label: 'HORIZON to borrowers', value: reserve.borrowHorizonAprBps > 0 ? fmtBps(reserve.borrowHorizonAprBps) : '—' },
            ]} />
          ) : (
            <StatGrid items={[
              { label: 'Max LTV', value: fmtBps(reserve.collateralFactorBps), help: 'The most you may borrow against this collateral when opening or increasing a position.' },
              { label: 'Liquidation threshold', value: fmtBps(reserve.liquidationThresholdBps), help: 'The point at which liquidation becomes possible. The gap above max LTV is deliberate breathing room.' },
              { label: 'Liquidation bonus', value: fmtBps(reserve.liquidationBonusBps), help: 'The discount a liquidator receives on seized collateral, which is what makes liquidating worth doing.' },
              { label: 'Liquidation fee', value: fmtBps(reserve.liquidationFeeBps) },
              { label: 'Supply cap', value: reserve.supplyCap > 0 ? `${fmt(reserve.supplyCap, 0)} ${reserve.symbol}` : 'Uncapped' },
              { label: 'Borrow cap', value: reserve.borrowCap > 0 ? `${fmt(reserve.borrowCap, 0)} ${reserve.symbol}` : 'Uncapped' },
              reserve.supplyCap > 0 ? { label: 'Supply headroom', value: `${fmt(Math.max(0, reserve.supplyCap - reserve.totalSupplied), 2)} ${reserve.symbol}` } : null,
              reserve.borrowCap > 0 ? { label: 'Borrow headroom', value: `${fmt(Math.max(0, reserve.borrowCap - reserve.totalBorrows), 2)} ${reserve.symbol}` } : null,
              reserve.isIsolated ? { label: 'Isolation ceiling', value: fmtUsd(reserve.isolationCeilingUsd) } : null,
              reserve.isIsolated ? { label: 'Isolation debt drawn', value: fmtUsd(reserve.isolationDebtUsd) } : null,
            ]} />
          )}
        </div>
      </Panel>

      <RateCurvePanel reserve={reserve} projection={projection} />
      <HistoryPanel reserve={reserve} />
    </>
  )
}

/// Recorded history, as opposed to the curve above it.
///
/// The two answer different questions and are deliberately both present: the curve says what the
/// contract WILL do at any utilisation, the history says where this reserve has actually been. A
/// page with only the curve cannot show that a rate has been volatile; a page with only the history
/// cannot answer "what happens if I borrow this much".
///
/// Nothing here is fabricated. A reserve with fewer than two recorded points renders an honest
/// "not yet" rather than a line through a single observation - see `useReserveHistory`.
function HistoryPanel({ reserve }) {
  const [metric, setMetric] = useState('rates')
  const [days, setDays] = useState(30)
  const { seriesFor, loading } = useReserveHistory()
  const series = seriesFor(reserve.asset, days)

  if (loading) return null
  if (series.length < 2) {
    return (
      <Panel style={{ padding: 28 }}>
        <SectionLabel>History</SectionLabel>
        <div style={{ marginTop: 10, color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.body, lineHeight: 1.6, maxWidth: 640 }}>
          Rate and liquidity history begins once this reserve has been sampled at least twice.
          Nothing is interpolated, so the line appears when there is genuinely something to draw.
        </div>
      </Panel>
    )
  }

  const lines = metric === 'rates'
    ? [
        { values: series.map((p) => p.supplyRateBps / 100), color: T.positive, label: 'Supply APY' },
        { values: series.map((p) => p.borrowRateBps / 100), color: T.accent, label: 'Borrow APR' },
      ]
    : metric === 'utilisation'
      ? [{ values: series.map((p) => p.utilizationBps / 100), color: T.accent, label: 'Utilisation' }]
      : [
          { values: series.map((p) => p.totalSupplied), color: T.positive, label: 'Supplied' },
          { values: series.map((p) => p.totalBorrows), color: T.warning, label: 'Borrowed' },
        ]

  const isPct = metric !== 'liquidity'

  return (
    <Panel style={{ padding: 28 }}>
      <div style={panelHeader}>
        <SectionLabel>History</SectionLabel>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <SubTabs
            tabs={[
              { id: 'rates', label: 'Rates' },
              { id: 'utilisation', label: 'Utilisation' },
              { id: 'liquidity', label: 'Supply & borrow' },
            ]}
            active={metric}
            onSelect={setMetric}
          />
          <SubTabs
            tabs={[{ id: 7, label: '7D' }, { id: 30, label: '30D' }, { id: 0, label: 'All' }]}
            active={days}
            onSelect={setDays}
          />
        </div>
      </div>

      <div style={{ marginTop: 20 }}>
        <MiniLineChart
          series={series}
          lines={lines}
          formatY={(v) => (isPct ? `${v.toFixed(1)}%` : fmt(v, 0))}
          includeZero
        />
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginTop: 12 }}>
        {lines.map((l) => (
          <span key={l.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.caption }}>
            <span style={{ width: 10, height: 2, background: l.color, flexShrink: 0 }} />
            {l.label}
          </span>
        ))}
        <span style={{ color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption }}>
          {series.length} recorded points
        </span>
      </div>
    </Panel>
  )
}

/// The reserve's own interest-rate curve.
///
/// This is the function the pool evaluates, sampled across the whole utilisation domain - the same
/// `poolMath` helpers that produce the live headline rates, so the curve cannot disagree with the
/// number beside it. A rate HISTORY says where the rate has been; the curve answers "what happens
/// if I borrow this much", which is the question actually being asked on this page. History is a
/// worthwhile addition later, and needs an indexer we have yet to build (see UI_PLAN.md).
function RateCurvePanel({ reserve, projection }) {
  const curve = buildRateCurve(reserve)
  if (curve.length === 0) return null

  const at = (utilBps) => curve.reduce(
    (best, p) => (Math.abs(p.utilizationBps - utilBps) < Math.abs(best.utilizationBps - utilBps) ? p : best),
    curve[0],
  )
  const current = at(reserve.utilizationBps)
  const projected = projection ? at(projection.utilizationBps) : null

  const adaptive = reserve.useAdaptiveIrm
  // The curve's corner: the 90% target for adaptive (where the rate == rate-at-target), or the
  // configured kink for the kinked fallback.
  const cornerPct = adaptive ? reserve.adaptiveTargetBps / 100 : reserve.kinkBps / 100
  const cornerPoint = at(Math.round(cornerPct * 100))

  const markers = [
    adaptive
      ? { x: cornerPct, y: cornerPoint.borrowRatePct, label: `Target · ${fmtPct(cornerPct)} · ${fmtBps(reserve.rateAtTargetBps)}`, color: T.accent, dashed: true }
      : null,
    { x: current.utilizationPct, y: current.borrowRatePct, label: `Now · ${fmtPct(current.utilizationPct)} used`, color: T.text },
    projected && Math.abs(projected.utilizationBps - current.utilizationBps) > 1
      ? { x: projected.utilizationPct, y: projected.borrowRatePct, label: `After your ${projection.side} · ${fmtPct(projected.utilizationPct)}`, color: T.warning, dashed: true }
      : null,
  ].filter(Boolean)

  return (
    <Panel style={{ padding: 28 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <SectionLabel>Interest rate model</SectionLabel>
        <span style={irmBadge}>{adaptive ? 'Adaptive' : 'Kinked'}</span>
      </div>
      <div style={{ margin: '8px 0 20px', color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.body, lineHeight: 1.6, maxWidth: 640 }}>
        {adaptive ? (
          <>
            Rates follow utilisation: the share of supplied {reserve.symbol} currently borrowed. The curve
            pivots at the {fmtPct(cornerPct)} target, where the borrow rate is the rate-at-target
            ({fmtBps(reserve.rateAtTargetBps)}); above it the rate climbs steeply to pull in supply,
            below it eases. What makes it adaptive: the whole curve drifts up while utilisation sits above
            target and down while it sits below, so it retunes to demand on its own rather than waiting on
            a governance vote. The gap between the two lines is the reserve factor.
          </>
        ) : (
          <>
            Rates follow utilisation: the share of supplied {reserve.symbol} currently borrowed. Past the
            kink at {fmtPct(cornerPct)} the borrow rate climbs steeply, which is what keeps liquidity
            available for suppliers who want to withdraw. The gap between the two lines is the reserve factor.
          </>
        )}
      </div>
      <CurveChart
        lines={[
          { points: curve.map((p) => ({ x: p.utilizationPct, y: p.borrowRatePct })), color: T.accent, label: 'Borrow APR' },
          { points: curve.map((p) => ({ x: p.utilizationPct, y: p.supplyRatePct })), color: T.positive, label: 'Supply APY' },
        ]}
        markers={markers}
        bands={cornerPct > 0 && cornerPct < 100
          ? [{ from: cornerPct, to: 100, color: T.warning }]
          : []}
        formatX={(v) => `${v.toFixed(0)}%`}
        formatY={(v) => `${v.toFixed(1)}%`}
      />
    </Panel>
  )
}

const irmBadge = {
  padding: '2px 8px', borderRadius: T.r.pill, border: `1px solid ${T.accent}55`, background: T.accentDim,
  color: T.accent, fontFamily: T.fontM, fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
}

// ─────────────────────────────────────────────────────────────────
// My Position
// ─────────────────────────────────────────────────────────────────

function PositionTab({ reserve, d, user }) {
  const { rate: horizonUsd, found: hasHorizonPrice } = useHorizonPriceUsd()

  const supplyRatePerSecond = reserve.totalSupplied > 0
    ? toNumber(reserve.supplyHorizonPerSecond) * (reserve.user.supplyBalance / reserve.totalSupplied)
    : 0
  const borrowRatePerSecond = reserve.totalBorrows > 0
    ? toNumber(reserve.borrowHorizonPerSecond) * (reserve.user.debt / reserve.totalBorrows)
    : 0
  const liveSupplyHorizon = useLiveAccrual(reserve.user.pendingSupplyHorizon, supplyRatePerSecond)
  const liveBorrowHorizon = useLiveAccrual(reserve.user.pendingBorrowHorizon, borrowRatePerSecond)

  const risk = accountRisk(d.reserves)
  const hasPosition = reserve.user.supplyBalance > 0 || reserve.user.debt > 0

  if (!user) {
    return <EmptyState title="Connect a wallet" body={`Your ${reserve.symbol} supply, debt and rewards appear here once a wallet is connected.`} />
  }
  // No CURRENT position still gets the activity panel: history outlives the position, and someone
  // who has closed one would otherwise lose the only route to their own record of it. Only a
  // disconnected wallet, above, has genuinely nothing to show.
  if (!hasPosition) {
    return (
      <>
        <EmptyState title="No open position in this market" body={`Supply or borrow ${reserve.symbol} from the panel beside this to open one.`} />
        <ActivityPanel reserve={reserve} />
      </>
    )
  }

  const supplyUsd = reserve.user.supplyBalance * reserve.priceUsd
  const debtUsd = reserve.user.debt * reserve.priceUsd

  return (
    <>
      <div style={cardsRow}>
        <Stat boxed label={`Supplied ${reserve.symbol}`} value={fmtUsd(supplyUsd)} sub={`${fmt(reserve.user.supplyBalance, 4)} ${reserve.symbol}`} />
        <Stat boxed label={`Borrowed ${reserve.symbol}`} value={fmtUsd(debtUsd)} sub={`${fmt(reserve.user.debt, 4)} ${reserve.symbol}`} />
        <Stat boxed label="Net in this market" value={fmtUsd(supplyUsd - debtUsd)} sub="supplied less borrowed" />
      </div>

      <Panel style={{ padding: 28 }}>
        <SectionLabel>Your position</SectionLabel>
        <div style={{ marginTop: 18 }}>
          <StatGrid items={[
            { label: 'Supply APY', value: fmtBps(reserve.totalSupplyAprBps), color: T.positive },
            { label: 'Borrow APR', value: fmtBps(reserve.totalBorrowAprBps) },
            {
              label: 'Account health factor',
              value: d.hasDebt && d.healthFactor != null ? (Number(d.healthFactor) / 1e18).toFixed(2) : 'No debt',
              help: 'One figure spanning every reserve: collateral anywhere protects debt anywhere.',
            },
            { label: 'Account collateral', value: fmtUsd(risk.rawCollateralUsd) },
            { label: 'Account debt', value: fmtUsd(risk.debtUsd) },
            { label: 'Available to borrow', value: fmtUsd(risk.availableBorrowsUsd) },
          ]} />
        </div>
        {/* Account-wide, so it is phrased as "your collateral" rather than naming this reserve -
            liquidation is triggered by the whole account, not by this market alone. */}
        <DropToLiquidation pct={risk.dropToLiquidationPct} symbol="your collateral" />
      </Panel>

      {(liveSupplyHorizon > 0 || liveBorrowHorizon > 0) && (
        <Panel style={{ padding: 28 }}>
          <SectionLabel>HORIZON rewards</SectionLabel>
          <div style={{ marginTop: 18 }}>
            <StatGrid items={[
              liveSupplyHorizon > 0 ? {
                label: 'Earned on your supply',
                value: <><span className="tabnum">{fmtBalance(liveSupplyHorizon, 4)}</span> HORIZON</>,
                color: T.positive,
              } : null,
              liveBorrowHorizon > 0 ? {
                label: 'Earned on your borrow',
                value: <><span className="tabnum">{fmtBalance(liveBorrowHorizon, 4)}</span> HORIZON</>,
                color: T.positive,
              } : null,
              hasHorizonPrice ? {
                label: 'Value',
                value: fmtUsd((liveSupplyHorizon + liveBorrowHorizon) * horizonUsd),
              } : null,
            ]} />
          </div>
          <div style={{ marginTop: 14 }}>
            <Link to="/portfolio" style={{ color: T.accent, fontFamily: T.fontM, fontSize: T.fs.caption, textDecoration: 'none' }}>
              Claim on Portfolio →
            </Link>
          </div>
        </Panel>
      )}

      <ActivityPanel reserve={reserve} />
    </>
  )
}

/// This wallet's own supplies, withdrawals, borrows and repayments against this reserve.
///
/// Reconstructed from event logs rather than an indexer, because it can be: `asset` and `user` are
/// both indexed on all four events, so the node does the filtering and a wallet with one
/// transaction downloads one log. That is why this needed no new backend while the RATE history
/// above did — rates leave no event to reconstruct, actions do.
function ActivityPanel({ reserve }) {
  const { rows, loading, error } = useReserveActivity(reserve.asset)

  if (loading) {
    return (
      <Panel style={{ padding: 28 }}>
        <SectionLabel>Your activity</SectionLabel>
        <div style={{ marginTop: 16 }}><Skeleton width="100%" height={64} /></div>
      </Panel>
    )
  }
  if (error || rows.length === 0) {
    return (
      <Panel style={{ padding: 28 }}>
        <SectionLabel>Your activity</SectionLabel>
        <div style={{ marginTop: 10, color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.body, lineHeight: 1.6 }}>
          {error
            ? 'This RPC endpoint returned no activity history. Switch RPC in your wallet and reload to see it.'
            : `Your ${reserve.symbol} supplies, withdrawals, borrows and repayments appear here once you make one.`}
        </div>
      </Panel>
    )
  }

  return (
    <Panel style={{ padding: 0 }}>
      <div style={{ padding: '24px 28px 0' }}>
        <SectionLabel>Your activity</SectionLabel>
      </div>
      <div style={{ overflowX: 'auto', marginTop: 14 }}>
        <table style={activityTable}>
          <thead>
            <tr>
              <th style={activityTh}>Action</th>
              <th style={{ ...activityTh, textAlign: 'right' }}>Amount</th>
              <th style={{ ...activityTh, textAlign: 'right' }}>When</th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 25).map((r) => (
              <tr key={`${r.txHash}-${r.logIndex}`}>
                <td style={activityTd}>
                  {/* Coloured by direction rather than by action name: supplying and repaying both
                      reduce risk, borrowing and withdrawing both raise it, and that is the thing
                      worth reading at a glance. */}
                  <span style={{ color: r.sign > 0 ? T.positive : T.warning, fontFamily: T.fontM, fontWeight: 700 }}>
                    {r.label}
                  </span>
                </td>
                <td style={{ ...activityTd, textAlign: 'right' }}>
                  <span className="mono">{fmt(toNumber(r.amount, reserve.decimals), 4)} {reserve.symbol}</span>
                </td>
                <td style={{ ...activityTd, textAlign: 'right', color: T.textDim }}>
                  {r.t ? new Date(r.t).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > 25 && (
        <div style={{ padding: '14px 28px 22px', color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption }}>
          Showing the 25 most recent of {rows.length}.
        </div>
      )}
    </Panel>
  )
}

// ─────────────────────────────────────────────────────────────────
// Info & FAQs
// ─────────────────────────────────────────────────────────────────

function InfoTab({ reserve }) {
  const feedInfo = usePriceFeedInfo(reserve.priceFeed)
  return (
    <div style={infoLayout}>
      <Panel style={{ padding: 28 }}>
        <SectionLabel>Frequently asked</SectionLabel>
        <div style={{ marginTop: 10 }}>
          <FaqAccordion items={reserveFaq(reserve)} />
        </div>
      </Panel>

      <Panel style={{ padding: 28, alignSelf: 'flex-start' }}>
        <SectionLabel>Asset details</SectionLabel>
        <div style={{ marginTop: 18 }}>
          <StatGrid columns={1} items={[
            { label: 'Symbol', value: reserve.symbol },
            { label: 'Decimals', value: String(reserve.decimals) },
            { label: 'Token', value: <AddressValue address={reserve.asset} /> },
            reserve.sTokenAddress ? { label: 'Receipt token', value: <AddressValue address={reserve.sTokenAddress} /> } : null,
            // Identified by probing the feed rather than by an address table - see usePriceFeedInfo.
            feedInfo.known ? { label: 'Oracle', value: feedInfo.provider, mono: false, help: feedInfo.description } : null,
            { label: 'Price feed', value: <AddressValue address={reserve.priceFeed} /> },
            { label: 'Price valid for', value: fmtDuration(reserve.priceStaleAfterSec), help: 'How long the pool treats a published price as current.' },
            { label: 'Hard price limit', value: fmtDuration(reserve.maxPriceAgeSec), help: 'Past this age the pool halts activity in this reserve rather than acting on a stale price.' },
            { label: 'Status', value: reserve.isPaused ? 'Paused' : reserve.isActive ? 'Active' : 'Inactive', color: reserve.isPaused ? T.warning : T.positive },
          ]} />
        </div>
      </Panel>
    </div>
  )
}

/// A contract address a lender or integrator actually wants to act on: opens in the block explorer,
/// and copies to the clipboard - the two affordances the old hover-only truncation was missing.
function AddressValue({ address }) {
  const [copied, setCopied] = useState(false)
  if (!address) return <span style={{ color: T.textDim }}>—</span>
  const copy = (e) => {
    e.preventDefault()
    navigator.clipboard?.writeText(address)
      .then(() => { setCopied(true); setTimeout(() => setCopied(false), 1200) })
      .catch(() => {})
  }
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
      <a href={addressLink(address)} target="_blank" rel="noreferrer" className="mono"
        style={{ color: T.accent, fontSize: T.fs.caption, textDecoration: 'none' }}
        title={`Open ${address} in explorer`}>
        {`${address.slice(0, 6)}…${address.slice(-4)}`}
      </a>
      <button onClick={copy} title="Copy address" aria-label="Copy address"
        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 1, color: copied ? T.positive : T.textDim, fontSize: 12 }}>
        {copied ? '✓' : '⧉'}
      </button>
    </span>
  )
}

function EmptyState({ title, body }) {
  return (
    <Panel elevated style={{ padding: 48, textAlign: 'center' }}>
      <div style={{ color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.ui, fontWeight: 700 }}>{title}</div>
      <div style={{ marginTop: 8, color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.body, lineHeight: 1.6 }}>{body}</div>
    </Panel>
  )
}

// ─────────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────────

// Main column plus a sticky action rail. Collapses to one column under 1100px, where a 380px rail
// would leave the tables too narrow to read.
const layout = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) minmax(320px, 380px)',
  gap: 24,
  alignItems: 'start',
}
const layoutNarrow = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr)',
  gap: 24,
  alignItems: 'start',
}
const rail = { minWidth: 0 }
const infoLayout = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
  gap: 20,
  alignItems: 'start',
}
const cardsRow = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: 12,
}
const panelHeader = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap',
}
const activityTable = { width: '100%', borderCollapse: 'collapse', minWidth: 420 }
const activityTh = {
  textAlign: 'left', padding: '10px 28px', color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption,
  fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', borderBottom: `1px solid ${T.border}`,
}
const activityTd = { padding: '12px 28px', borderBottom: `1px solid ${T.border}`, color: T.text, fontFamily: T.fontM, fontSize: T.fs.body }
const backLinkStyle = {
  color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.body, textDecoration: 'none',
}
